import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";

const REGISTER_BG = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg0MTgwNDgwfDA&ixlib=rb-4.1.0&q=85";

const STEPS = ["Dati Azienda", "Dati Titolare", "Policy"];

export default function Register() {
  const nav = useNavigate();
  const { googleLogin } = useAuth();
  // Method chooser: mostrato prima del wizard se l'utente non è arrivato da callback Google
  // e non ha ancora scelto il metodo di registrazione.
  const [methodChosen, setMethodChosen] = useState(false);
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [policyScrolled, setPolicyScrolled] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cityQuery, setCityQuery] = useState("");
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  // Iter209dz5 — Progressive disclosure Fase 1: fino a quando non abbiamo
  // verificato la P.IVA via VIES mostriamo solo Tipo di attivita' + P.IVA.
  // "idle" | "loading" | "found" | "not_found".
  const [vatCheckState, setVatCheckState] = useState("idle");
  const [vatCheckMessage, setVatCheckMessage] = useState("");
  // Iter209dza — Errore inline su P.IVA quando l utente preme Avanti con meno di 11 cifre.
  const [vatInlineError, setVatInlineError] = useState("");
  // Google prefill (se l'utente arriva da GoogleCallback dopo login Google e non è ancora registrato)
  const [googlePrefill, setGooglePrefill] = useState(null);
  const [form, setForm] = useState({
    // Fase 1
    business_type_id: "",
    company_name: "", company_toponimo: "", company_address: "", company_civico: "",
    company_city: "", company_province: "", company_cap: "", company_insegna: "",
    company_phone: "", company_vat: "",
    company_email: "", company_pec: "",
    company_sdi: "", company_sdi_not_available: false,
    // Fase 2
    first_name: "", last_name: "", owner_phone: "", owner_email: "",
    // Fase 3 (credenziali + policy)
    email: "", password: "", policy_accepted: false,
  });

  // Carica dati Google da sessionStorage se presenti (una tantum al mount)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("ht_google_prefill");
      if (!raw) return;
      const g = JSON.parse(raw);
      if (!g?.email) return;
      setGooglePrefill(g);
      setMethodChosen(true); // salta il chooser: la scelta è già stata fatta (Google)
      setForm(f => ({
        ...f,
        email: g.email,
        first_name: g.first_name || f.first_name,
        last_name: g.last_name || f.last_name,
        owner_email: g.email,
        company_email: g.email,
      }));
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    api.get("/professional-categories").then(r => setCategories(r.data));
    api.get("/public/policy").then(r => setPolicy(r.data));
  }, []);

  // Debounced città
  useEffect(() => {
    if (!cityDropdownOpen) return;
    const t = setTimeout(async () => {
      const q = (cityQuery || "").trim();
      try {
        const r = await api.get(`/public/cities/search?q=${encodeURIComponent(q)}&limit=15`);
        setCitySuggestions(r.data || []);
      } catch { setCitySuggestions([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [cityQuery, cityDropdownOpen]);

  const pickCity = (c) => {
    setForm(f => ({ ...f, company_city: c.name, company_province: c.province, company_cap: c.cap }));
    setCityQuery(c.name);
    setCityDropdownOpen(false);
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const canNextStep1 = form.business_type_id && form.company_name && form.company_toponimo && form.company_address && form.company_civico && form.company_city && form.company_province && form.company_cap && form.company_phone && form.company_vat && form.company_email && form.company_pec && (form.company_sdi_not_available || form.company_sdi.trim().length === 7);
  const canNextStep2 = form.first_name && form.last_name && form.owner_phone;
  const effectiveEmail = form.email || form.company_email;
  const isGoogleFlow = Boolean(googlePrefill?.pending_token);
  const passwordValid = isGoogleFlow || form.password.length >= 6;
  const canSubmit = effectiveEmail && passwordValid && form.policy_accepted && policyScrolled;

  // Iter209dz7 — Parser dell'indirizzo VIES in {toponimo, indirizzo, civico, city, province, cap}.
  //
  // VIES ritorna una stringa tipo:
  //   "VIA SANT'EUROSIA 24, 20811 CESANO MADERNO MB"
  //   "VIA SANT'EUROSIA 24\n20811 CESANO MADERNO MB"
  // (il backend `_clean_address` collassa le righe in "A, B").
  //
  // Strategia:
  //  1. Split su virgola o CAP a 5 cifre → `viaLine` e `cityLine`.
  //  2. `viaLine`: la prima parola e' il toponimo (VIA/PIAZZA/CORSO...), l'ultima
  //     e' il civico (se contiene cifre); il resto in mezzo e' l'indirizzo.
  //  3. `cityLine`: 5 cifre CAP all'inizio + 2 lettere maiuscole provincia alla
  //     fine; in mezzo la citta'.
  //  4. Se un pezzo non e' riconosciuto, lo lasciamo vuoto cosi' l'utente lo compila.
  const parseViesAddress = (addr) => {
    const out = { toponimo: "", indirizzo: "", civico: "", city: "", province: "", cap: "" };
    if (!addr || typeof addr !== "string") return out;
    const s = addr.trim();
    let viaLine = "", cityLine = "";
    if (s.includes(",")) {
      const parts = s.split(",").map((x) => x.trim()).filter(Boolean);
      viaLine = parts[0] || "";
      cityLine = parts.slice(1).join(", ").trim();
    } else {
      const m = s.match(/\b(\d{5})\b/);
      if (m) {
        viaLine = s.slice(0, m.index).trim().replace(/[,;]$/, "").trim();
        cityLine = s.slice(m.index).trim();
      } else {
        viaLine = s;
      }
    }
    // ===== Via line =====
    if (viaLine) {
      const TOPONIMI = [
        "VIA", "V.", "VLE", "VIALE", "PIAZZA", "P.ZA", "PZA", "PIAZZALE",
        "P.LE", "PLE", "CORSO", "CSO", "C.SO", "STRADA", "STR.", "LARGO",
        "LGO", "L.GO", "VICOLO", "VIC.", "ROTONDA", "LUNGARNO", "LUNGOMARE",
        "LUNG.MARE", "FRAZIONE", "FRAZ.", "LOCALITA", "LOCALITA'", "LOC.",
        "SALITA", "DISCESA", "GALLERIA", "CONTRADA", "C.DA", "BORGO", "CALLE",
        "RIVIERA", "RIO", "RAMPA", "ALZAIA", "PONTE",
      ];
      const words = viaLine.split(/\s+/).filter(Boolean);
      if (words.length > 0) {
        const first = words[0].toUpperCase().replace(/[.,]+$/, "");
        const normFirst = first.replace(/\./g, "");
        const match = TOPONIMI.find((t) => t.replace(/\./g, "") === normFirst);
        if (match) {
          out.toponimo = words.shift();
        }
      }
      // Civico: ultima parola, deve contenere almeno una cifra ed essere corta (<=12).
      if (words.length > 0) {
        const last = words[words.length - 1].replace(/[,;]+$/, "");
        if (/\d/.test(last) && last.length <= 12 && /^[\d/A-Za-z\-]+$/.test(last)) {
          out.civico = last;
          words.pop();
        }
      }
      if (words.length > 0) {
        out.indirizzo = words.join(" ").replace(/[,;]+$/, "").trim();
      }
    }
    // ===== City line (CAP CITTA PROV) =====
    if (cityLine) {
      const capMatch = cityLine.match(/^(\d{5})\s+(.+)$/);
      if (capMatch) {
        out.cap = capMatch[1];
        const rest = capMatch[2].trim();
        const provMatch = rest.match(/^(.+?)\s+([A-Z]{2})$/);
        if (provMatch) {
          out.city = provMatch[1].replace(/[,;]+$/, "").trim();
          out.province = provMatch[2];
        } else {
          out.city = rest.replace(/[,;]+$/, "").trim();
        }
      } else {
        // Nessun CAP: proviamo almeno la provincia a 2 lettere in coda.
        const provMatch = cityLine.match(/^(.+?)\s+([A-Z]{2})$/);
        if (provMatch) {
          out.city = provMatch[1].replace(/[,;]+$/, "").trim();
          out.province = provMatch[2];
        } else {
          out.city = cityLine.replace(/[,;]+$/, "").trim();
        }
      }
    }
    return out;
  };

  // Pre-riempi form.email con company_email quando entriamo nello step 3
  useEffect(() => {
    if (step === 2 && !form.email && form.company_email) {
      setForm(f => ({ ...f, email: f.company_email }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Iter209dz5 — Auto-lookup VIES al raggiungimento di 11 cifre nella P.IVA.
  // Usiamo l'endpoint pubblico `/public/vat/lookup` (no auth) perche'
  // qui l'utente non e' ancora registrato. Debounce 700ms.
  useEffect(() => {
    const raw = (form.company_vat || "").replace(/\D/g, "");
    if (raw.length !== 11) {
      // L'utente sta ancora digitando o ha cancellato → nascondi di nuovo gli altri campi.
      if (vatCheckState !== "idle") {
        setVatCheckState("idle");
        setVatCheckMessage("");
      }
      return;
    }
    setVatCheckState("loading");
    setVatCheckMessage("");
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/public/vat/lookup`, { params: { vat: raw } });
        if (r.data?.found && r.data?.company) {
          const c = r.data.company;
          const parsed = parseViesAddress(c.address || "");
          setForm(f => ({
            ...f,
            // Prepopola solo se l'utente non ha gia' editato manualmente.
            company_name: f.company_name || c.name || "",
            company_toponimo: f.company_toponimo || parsed.toponimo || "",
            company_address: f.company_address || parsed.indirizzo || "",
            company_civico: f.company_civico || parsed.civico || "",
            company_city: f.company_city || parsed.city || "",
            company_province: f.company_province || parsed.province || "",
            company_cap: f.company_cap || parsed.cap || "",
          }));
          // Sincronizza il text input della citta' (usa stato separato cityQuery).
          if (parsed.city && !cityQuery) setCityQuery(parsed.city);
          setVatCheckState("found");
          setVatCheckMessage(`P.IVA valida: ${c.name}`);
        } else {
          setVatCheckState("not_found");
          setVatCheckMessage(
            "P.IVA non trovata nel registro europeo — inserisci i dati manualmente"
          );
        }
      } catch (_e) {
        setVatCheckState("not_found");
        setVatCheckMessage(
          "P.IVA non trovata nel registro europeo — inserisci i dati manualmente"
        );
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.company_vat]);

  const onPolicyScroll = (e) => {
    const el = e.currentTarget;
    // consider scrolled when within 20px of bottom
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setPolicyScrolled(true);
  };

  const next = () => {
    if (step === 0) {
      // Iter209dza — Validazione P.IVA 11 cifre con errore inline (bloccante).
      const vatDigits = (form.company_vat || "").replace(/\D/g, "");
      if (vatDigits.length !== 11) {
        setVatInlineError("La P.IVA italiana deve essere di 11 cifre");
        return;
      }
      setVatInlineError("");
      if (!canNextStep1) { toast.error("Compila tutti i campi obbligatori"); return; }
    }
    if (step === 1 && !canNextStep2) { toast.error("Compila tutti i campi obbligatori"); return; }
    setStep(s => s + 1);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      // Default email principale = azienda se non specificata
      const finalEmail = form.email || form.company_email;
      const payload = { ...form, email: finalEmail };
      if (isGoogleFlow) {
        payload.auth_provider = "google";
        payload.google_pending_token = googlePrefill.pending_token;
        payload.avatar_url = googlePrefill.picture || null;
        payload.password = "";  // ignorato dal backend quando provider=google
      }
      await api.post("/auth/register", payload);
      // Cleanup Google prefill in caso di successo
      if (isGoogleFlow) {
        try { sessionStorage.removeItem("ht_google_prefill"); } catch (_) { /* ignore */ }
      }
      toast.success("Registrazione ricevuta");
      nav("/pending");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Form panel */}
      <div className="flex items-center justify-center p-4 md:p-8 overflow-y-auto relative"
        style={{ background: "linear-gradient(135deg, #1a0f3a 0%, #2d1b5c 50%, #1a3563 100%)" }}>
        <Link
          to="/"
          className="absolute top-4 left-4 md:top-6 md:left-6 inline-flex items-center gap-1.5 text-xs font-semibold text-white/70 hover:text-white transition-colors z-10"
          data-testid="register-back-home"
        >
          <ArrowLeft size={14} weight="bold" />
          Torna alla home
        </Link>
        <div className="w-full max-w-2xl fade-up py-6">
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <img src="/logo.png" alt="Housetag" className="w-12 h-12" />
            </Link>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight ht-gradient-text">
              Crea il tuo Account
            </h1>
            {methodChosen && (
              <p className="text-white/70 mt-2 text-sm">Fase {step + 1} di {STEPS.length}: {STEPS[step]}</p>
            )}
            {!methodChosen && (
              <p className="text-white/70 mt-2 text-sm">Scegli come registrarti</p>
            )}
          </div>

        {/* Stepper */}
        {methodChosen && (
          <div className="flex justify-center gap-2 mb-6">
            {STEPS.map((s, i) => (
              <div key={s?.key || s?.label || `step-${i}`} className={`h-1 w-16 rounded-full transition-all ${i <= step ? "bg-gradient-to-r from-purple-500 to-blue-500" : "bg-white/10"}`} />
            ))}
          </div>
        )}

        {!methodChosen && (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4" data-testid="reg-trust-strip">
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-white/[0.03] border border-white/10">
                <div className="text-2xl">🔒</div>
                <div className="text-[11px] text-white/80 font-semibold text-center leading-tight">
                  Registrazione<br/>sicura
                </div>
              </div>
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-white/[0.03] border border-white/10">
                <div className="text-2xl">⏱️</div>
                <div className="text-[11px] text-white/80 font-semibold text-center leading-tight">
                  Meno di<br/>2 minuti
                </div>
              </div>
              <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-white/[0.03] border border-white/10">
                <div className="text-2xl">🎁</div>
                <div className="text-[11px] text-white/80 font-semibold text-center leading-tight">
                  Nessuna<br/>carta richiesta
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 backdrop-blur bg-white/[0.03] p-6 md:p-8 shadow-2xl space-y-4" data-testid="reg-method-chooser">
            <button
              type="button"
              onClick={() => setMethodChosen(true)}
              className="w-full flex items-start gap-4 p-5 rounded-xl border-2 border-white/10 bg-white/[0.02] hover:border-purple-400 hover:bg-white/[0.05] transition-all text-left group"
              data-testid="reg-method-email"
            >
              <div className="w-11 h-11 rounded-lg bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-2xl shrink-0">
                ✉️
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white text-base">Registrati con email e password</div>
                <div className="text-xs text-white/60 mt-1 leading-relaxed">
                  Compila il modulo con i dati della tua attività, scegli una password sicura e conferma l'email.
                </div>
              </div>
              <ArrowRight size={20} className="text-white/40 group-hover:text-purple-300 mt-2 shrink-0" />
            </button>

            <button
              type="button"
              onClick={googleLogin}
              className="w-full flex items-start gap-4 p-5 rounded-xl border-2 border-white/10 bg-white/[0.02] hover:border-blue-400 hover:bg-white/[0.05] transition-all text-left group"
              data-testid="reg-method-google"
            >
              <div className="w-11 h-11 rounded-lg bg-white flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true">
                  <path fill="#4285F4" d="M23.49 12.27c0-.86-.08-1.68-.22-2.47H12v4.68h6.44c-.28 1.5-1.13 2.77-2.4 3.62v3.01h3.88c2.27-2.09 3.57-5.17 3.57-8.84z" />
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.92l-3.88-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.96H1.28v3.11C3.26 21.3 7.31 24 12 24z" />
                  <path fill="#FBBC05" d="M5.27 14.26c-.24-.72-.38-1.49-.38-2.26s.14-1.54.38-2.26V6.63H1.28C.46 8.24 0 10.06 0 12s.46 3.76 1.28 5.37l3.99-3.11z" />
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.94 1.15 15.23 0 12 0 7.31 0 3.26 2.7 1.28 6.63l3.99 3.11C6.22 6.86 8.87 4.75 12 4.75z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white text-base">Registrati con Google</div>
                <div className="text-xs text-white/60 mt-1 leading-relaxed">
                  Autorizza l'accesso con il tuo account Google. Email, nome e cognome pre-compilati automaticamente.
                </div>
              </div>
              <ArrowRight size={20} className="text-white/40 group-hover:text-blue-300 mt-2 shrink-0" />
            </button>

            <div className="text-center text-xs text-white/50 pt-2">
              Hai già un account? <Link to="/login" className="text-purple-300 hover:text-purple-200 underline">Accedi qui</Link>
            </div>
          </div>
          </>
        )}

        {methodChosen && (
        <div className="rounded-2xl border border-white/10 backdrop-blur bg-white/[0.03] p-6 md:p-8 shadow-2xl">
          {step === 0 && (
            <div className="space-y-3" data-testid="reg-step-1">
              <div>
                <FieldLabel required>Tipo di attività</FieldLabel>
                <select
                  value={form.business_type_id}
                  onChange={upd("business_type_id")}
                  className="w-full h-11 rounded-md border border-white/10 bg-slate-900 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  style={{ colorScheme: "dark" }}
                  data-testid="reg-business-type"
                >
                  <option value="" style={{ backgroundColor: "#1e1b4b", color: "#ffffff" }}>Seleziona il tipo di attività</option>
                  {categories.map(c => (
                    <option
                      key={c._id}
                      value={c._id}
                      style={{ backgroundColor: "#1e1b4b", color: "#ffffff" }}
                    >
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* Iter209dz5 — P.IVA mostrata subito; il resto dei campi
                  si rivela solo dopo il lookup VIES (found o not_found). */}
              <div>
                <DarkField
                  label="P.IVA"
                  required
                  value={form.company_vat}
                  onChange={(e) => {
                    upd("company_vat")(e);
                    // Rimuovi l errore inline appena l utente ricomincia a digitare.
                    if (vatInlineError) setVatInlineError("");
                  }}
                  placeholder="es. 01234567890 (11 cifre)"
                  testid="reg-company-vat"
                />
                {(() => {
                  const digits = (form.company_vat || "").replace(/\D/g, "").length;
                  const ok = digits === 11;
                  const toneClass = ok ? "text-emerald-300" : digits > 11 ? "text-rose-300" : "text-white/60";
                  return (
                    <p className={`text-[11px] mt-1 ${toneClass}`} data-testid="reg-company-vat-counter">
                      {digits}/11 cifre{ok ? " ✓" : digits > 11 ? " · troppe cifre" : ""}
                    </p>
                  );
                })()}
                {vatInlineError && (
                  <p className="text-[11px] text-rose-400 mt-1" data-testid="reg-company-vat-error">
                    {vatInlineError}
                  </p>
                )}
                {vatCheckState === "loading" && (
                  <p className="text-[11px] text-white/70 mt-1 flex items-center gap-1" data-testid="reg-vat-loading">
                    <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Verifica P.IVA in corso presso il registro europeo (VIES)…
                  </p>
                )}
                {vatCheckState === "found" && (
                  <p className="text-[11px] text-emerald-300 mt-1" data-testid="reg-vat-found">
                    ✓ {vatCheckMessage}
                  </p>
                )}
                {vatCheckState === "not_found" && (
                  <p className="text-[11px] text-amber-300 mt-1" data-testid="reg-vat-not-found">
                    {vatCheckMessage}
                  </p>
                )}
                {vatCheckState === "idle" && (
                  <p className="text-[11px] text-white/50 mt-1">
                    Alla digitazione di 11 cifre interroghiamo il registro europeo (VIES) per prepopolare i dati aziendali.
                  </p>
                )}
              </div>

              {(vatCheckState === "found" || vatCheckState === "not_found") && (
                <div className="space-y-3" data-testid="reg-step-1-fields">
                  <DarkField label="Nome azienda" required value={form.company_name} onChange={upd("company_name")} placeholder="es. Rossi S.r.l." testid="reg-company-name" />
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_100px] gap-3">
                    <DarkField label="Toponimo" required value={form.company_toponimo} onChange={upd("company_toponimo")} placeholder="es. Via, Piazza" />
                    <DarkField label="Indirizzo" required value={form.company_address} onChange={upd("company_address")} placeholder="es. Garibaldi" />
                    <DarkField label="Civico" required value={form.company_civico} onChange={upd("company_civico")} placeholder="es. 12/B" />
                  </div>
                  <div className="relative">
                    <FieldLabel required>Città</FieldLabel>
                    <input required type="text" value={cityQuery}
                      onChange={e => {
                        const v = e.target.value;
                        setCityQuery(v);
                        setForm({ ...form, company_city: v, company_province: "", company_cap: "" });
                        setCityDropdownOpen(true);
                      }}
                      onFocus={() => setCityDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setCityDropdownOpen(false), 150)}
                      autoComplete="off"
                      placeholder="Inizia a digitare e seleziona…"
                      className="w-full h-11 rounded-md bg-white/10 border border-white/20 px-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      data-testid="reg-city-input" />
                    {cityDropdownOpen && citySuggestions.length > 0 && (
                      <div className="absolute z-30 mt-1 w-full bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto text-gray-900">
                        {citySuggestions.map(c => (
                          <button key={`${c.name}-${c.province}`} type="button"
                            onMouseDown={(e) => { e.preventDefault(); pickCity(c); }}
                            className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm flex items-center justify-between"
                            data-testid={`reg-city-opt-${c.province}`}>
                            <span className="font-medium">{c.name}</span>
                            <span className="text-xs text-muted-foreground">{c.province} · {c.cap}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {form.company_city && !form.company_province && (
                      <p className="text-[11px] text-amber-300 mt-1">↑ Seleziona una città dall'elenco per confermare</p>
                    )}
                  </div>
                  {(form.company_province || form.company_cap) && (
                    <div className="grid grid-cols-2 gap-3">
                      <DarkField label="Provincia" value={form.company_province} onChange={() => {}} disabled />
                      <DarkField label="CAP" value={form.company_cap} onChange={() => {}} disabled />
                    </div>
                  )}
                  {/* Iter209dzl — Rimosso il campo "Insegna (se diversa da nome
                      azienda)" dal wizard di registrazione. La chiave
                      `company_insegna` resta nello state (default stringa vuota)
                      per non rompere eventuali consumer backend. */}
                  <DarkField label="Telefono azienda" required value={form.company_phone} onChange={upd("company_phone")} placeholder="es. +39 02 1234567" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <DarkField label="Mail Azienda" required type="email" value={form.company_email} onChange={upd("company_email")} placeholder="es. info@azienda.it" testid="reg-company-email" />
                    <DarkField label="PEC" required type="email" value={form.company_pec} onChange={upd("company_pec")} placeholder="es. azienda@pec.it" />
                  </div>
                  {/* Iter209bf — Codice SDI (7 caratteri) obbligatorio per fatturazione elettronica, con flag "Non lo ho" */}
                  <div>
                    <DarkField
                      label="Codice SDI (Codice Destinatario)"
                      required={!form.company_sdi_not_available}
                      value={form.company_sdi}
                      onChange={(e) => {
                        const v = (e?.target?.value ?? e ?? "").toUpperCase().slice(0, 7);
                        setForm({ ...form, company_sdi: v });
                      }}
                      placeholder="7 caratteri alfanumerici — es. ABC1234"
                      disabled={form.company_sdi_not_available}
                      testid="reg-company-sdi"
                    />
                    {!form.company_sdi_not_available && (() => {
                      const n = (form.company_sdi || "").length;
                      const ok = n === 7;
                      const toneClass = ok ? "text-emerald-300" : "text-white/60";
                      return (
                        <p className={`text-[11px] mt-1 ${toneClass}`} data-testid="reg-company-sdi-counter">
                          {n}/7 caratteri{ok ? " ✓" : ""}
                        </p>
                      );
                    })()}
                    <label className="mt-2 flex items-center gap-2 text-xs text-white/80 select-none cursor-pointer" data-testid="reg-company-sdi-not-available-label">
                      <input
                        type="checkbox"
                        checked={form.company_sdi_not_available}
                        onChange={(e) => setForm({
                          ...form,
                          company_sdi_not_available: e.target.checked,
                          company_sdi: e.target.checked ? "" : form.company_sdi,
                        })}
                        className="w-4 h-4 accent-purple-500"
                        data-testid="reg-company-sdi-not-available"
                      />
                      <span>Non lo ho / Non applicabile (privati senza fatturazione elettronica)</span>
                    </label>
                    {!form.company_sdi_not_available && form.company_sdi && form.company_sdi.length !== 7 && (
                      <p className="text-[11px] text-amber-300 mt-1" data-testid="reg-company-sdi-hint">
                        Il Codice SDI deve essere esattamente 7 caratteri alfanumerici.
                      </p>
                    )}
                  </div>
                </div>
              )}
              <Button onClick={next} className="w-full h-12 mt-3 ht-gradient border-0 text-base" data-testid="reg-next-1" disabled={vatCheckState === "idle" || vatCheckState === "loading"}>
                Avanti <ArrowRight size={16} weight="bold" className="ml-2" />
              </Button>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="reg-step-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DarkField label="Nome Titolare" required value={form.first_name} onChange={upd("first_name")} placeholder="es. Mario" testid="reg-first-name" />
                <DarkField label="Cognome Titolare" required value={form.last_name} onChange={upd("last_name")} placeholder="es. Rossi" testid="reg-last-name" />
              </div>
              <DarkField label="Cellulare Titolare" required type="tel" value={form.owner_phone} onChange={upd("owner_phone")} placeholder="es. +39 333 1234567" testid="reg-owner-phone" />
              <DarkField label="Mail Titolare (se diversa dalla principale)" type="email" value={form.owner_email} onChange={upd("owner_email")} placeholder="es. m.rossi@azienda.it" />
              <div className="flex gap-2 pt-3">
                <Button variant="outline" onClick={() => setStep(0)} className="flex-1 h-12 bg-white/5 border-white/20 text-white hover:bg-white/10">
                  <ArrowLeft size={16} className="mr-2" /> Indietro
                </Button>
                <Button onClick={next} className="flex-1 h-12 ht-gradient border-0" data-testid="reg-next-2">
                  Prosegui verso la policy <ArrowRight size={16} weight="bold" className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={submit} className="space-y-3" data-testid="reg-step-3">
              <div className="text-white/80 text-sm mb-3">Ultimo passaggio: leggi la policy fino in fondo, {isGoogleFlow ? "conferma la tua email Google" : "crea le credenziali"} e completa.</div>
              {isGoogleFlow && (
                <div className="p-3 rounded-md bg-purple-500/15 border border-purple-400/30 text-xs text-white/90 flex items-start gap-3" data-testid="google-flow-banner">
                  <div className="mt-0.5">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-purple-300"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-5-5 1.41-1.41L11 14.17l7.59-7.59L20 8l-9 9z"/></svg>
                  </div>
                  <div>
                    <div className="font-semibold text-white">Registrazione con Google</div>
                    <div className="text-white/70">L'accesso userà il tuo account Google <strong className="text-white">{googlePrefill?.email}</strong>. Non ti serve una password.</div>
                  </div>
                </div>
              )}
              <DarkField
                label="Email di accesso"
                required
                type="email"
                value={form.email}
                onChange={upd("email")}
                placeholder="es. info@azienda.it"
                testid="reg-email"
                disabled={isGoogleFlow}
                badge={isGoogleFlow ? "Gestita da Google" : null}
              />
              {!isGoogleFlow && (
                <div>
                  <div className="mb-1 text-xs uppercase tracking-widest text-white/70">Password (min 6 caratteri) *</div>
                  <div className="relative">
                    <input required type={showPwd ? "text" : "password"} value={form.password}
                      onChange={upd("password")} placeholder="••••••••"
                      className="w-full h-11 rounded-md bg-white/10 border border-white/20 px-3 pr-10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-400"
                      data-testid="reg-password" />
                    <button type="button" onClick={() => setShowPwd(v => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-1"
                      aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                      data-testid="toggle-reg-password">
                      {showPwd ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <FieldLabel required>Informativa Privacy e Termini</FieldLabel>
                <div
                  onScroll={onPolicyScroll}
                  className="p-4 rounded-md bg-white/5 border border-white/10 text-xs text-white/80 max-h-56 overflow-y-auto whitespace-pre-wrap leading-relaxed"
                  data-testid="policy-scroll-area"
                >
                  {policy ? (
                    <>
                      <div className="font-semibold text-white mb-2 text-sm">{policy.title} — v{policy.version}</div>
                      {policy.body}
                    </>
                  ) : "Caricamento policy..."}
                </div>
                <div className="text-[11px] text-white/60 mt-1.5 flex items-center gap-1.5">
                  {policyScrolled ? (
                    <><CheckCircle size={12} weight="fill" className="text-green-400" /> Hai letto l'informativa completa</>
                  ) : (
                    <>↓ Scorri fino in fondo per abilitare l'accettazione</>
                  )}
                </div>
              </div>

              <label className={`flex items-start gap-2 text-sm text-white ${policyScrolled ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`} data-testid="reg-policy-label">
                <input type="checkbox" required disabled={!policyScrolled} checked={form.policy_accepted}
                  onChange={e => setForm({ ...form, policy_accepted: e.target.checked })}
                  className="mt-1"
                  data-testid="reg-policy" />
                <span>Accetto l'informativa privacy e i termini di utilizzo di Housetag.it (v{policy?.version || "1.0"}). Riceverò copia via email e PEC.</span>
              </label>

              <div className="flex gap-2 pt-3">
                <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 h-12 bg-white/5 border-white/20 text-white hover:bg-white/10">
                  <ArrowLeft size={16} className="mr-2" /> Indietro
                </Button>
                <Button type="submit" disabled={!canSubmit || loading} className="flex-1 h-12 ht-gradient border-0" data-testid="reg-submit">
                  {loading ? "Invio..." : "Completa registrazione"} <CheckCircle size={16} weight="bold" className="ml-2" />
                </Button>
              </div>
            </form>
          )}
        </div>
        )}

        {methodChosen && (
          <p className="text-sm text-center mt-6 text-white/70">
            Hai già un account?{" "}
            <Link to="/login" className="text-purple-300 font-semibold hover:text-white">Accedi</Link>
          </p>
        )}
        </div>
      </div>

      {/* Image panel — different from login */}
      <div className="hidden md:block relative overflow-hidden" data-testid="register-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${REGISTER_BG})` }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(140deg, rgba(26,15,58,0.55) 0%, rgba(45,27,92,0.65) 50%, rgba(26,53,99,0.75) 100%)" }} />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="max-w-md">
            <div className="label-eyebrow text-white/80">Entra nella rete</div>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-3">
              Fai crescere il tuo studio con Housetag.
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed">
              Accedi al marketplace di servizi tecnici, gestisci pratiche e clienti in un unico posto.
              Registrati in 3 semplici passaggi.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children, required, badge }) {
  return (
    <label className="flex items-center gap-2 flex-wrap text-sm text-white/85 mb-1.5">
      <span>{children} {required && <span className="text-purple-400">*</span>}</span>
      {badge && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-blue-500/15 border border-blue-300/40 px-1.5 py-0.5 text-[10px] font-semibold text-blue-100"
          data-testid="google-managed-badge"
          title="Questo campo è gestito da Google e non è modificabile"
        >
          <svg viewBox="0 0 24 24" width={10} height={10} aria-hidden="true">
            <path fill="#4285F4" d="M23.49 12.27c0-.86-.08-1.68-.22-2.47H12v4.68h6.44c-.28 1.5-1.13 2.77-2.4 3.62v3.01h3.88c2.27-2.09 3.57-5.17 3.57-8.84z" />
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.92l-3.88-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.96H1.28v3.11C3.26 21.3 7.31 24 12 24z" />
            <path fill="#FBBC05" d="M5.27 14.26c-.24-.72-.38-1.49-.38-2.26s.14-1.54.38-2.26V6.63H1.28C.46 8.24 0 10.06 0 12s.46 3.76 1.28 5.37l3.99-3.11z" />
            <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.94 1.15 15.23 0 12 0 7.31 0 3.26 2.7 1.28 6.63l3.99 3.11C6.22 6.86 8.87 4.75 12 4.75z" />
          </svg>
          {badge}
        </span>
      )}
    </label>
  );
}

function DarkField({ label, required, value, onChange, placeholder, type = "text", testid, disabled = false, badge }) {
  return (
    <div>
      <FieldLabel required={required} badge={badge}>{label}</FieldLabel>
      <Input type={type} required={required} value={value} onChange={onChange} placeholder={placeholder}
        data-testid={testid} disabled={disabled}
        className={`h-11 bg-black/20 border-white/10 text-white placeholder:text-white/40 ${disabled ? "opacity-70 cursor-not-allowed" : ""}`} />
    </div>
  );
}
